import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/use-auth";
import { canDo } from "@shared/roles";
import { ContactSelector, CreateContactModal } from "@/components/ContactSelector";
import type { Contact } from "@/components/ContactSelector";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "wouter";
import { Plus, Trash2, Link2, Pencil, ChevronDown, ChevronRight, Loader2, ExternalLink, Search, X, ChevronsUpDown, Users, User } from "lucide-react";

const CATEGORIE_LABELS: Record<string, string> = {
  membre_equipe: "Équipe",
  intervenant_exterieur: "Externe",
};

const CATEGORIE_COLORS: Record<string, string> = {
  membre_equipe: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
  intervenant_exterieur: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
};

const emptyToNull = (v: unknown) => (v === "" || v === undefined ? null : v);

// ─── EntiteOption ─────────────────────────────────────────────────────────────

interface EntiteOption {
  id: string;
  label: string;
  sousTitre?: string;
}

// ─── EntiteCombobox — Popover+Input combobox replacing <Select> for entity ────

interface EntiteComboboxProps {
  options: EntiteOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  loading?: boolean;
  placeholder?: string;
  testId?: string;
}

function EntiteCombobox({ options, value, onChange, loading, placeholder = "Choisir…", testId }: EntiteComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.id === value);

  const filtered = query.trim()
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.sousTitre && o.sousTitre.toLowerCase().includes(query.toLowerCase()))
      )
    : options;

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={testId}
        >
          {loading ? (
            <span className="text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />Chargement…
            </span>
          ) : selected ? (
            <span className="truncate font-medium">{selected.label}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <div className="flex items-center border-b px-2 py-1.5">
          <Search className="h-3.5 w-3.5 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            placeholder="Rechercher…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              {loading ? "Chargement…" : "Aucun résultat"}
            </p>
          ) : (
            filtered.map(o => (
              <button
                key={o.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex flex-col gap-0.5 ${value === o.id ? "bg-primary/5" : ""}`}
                onClick={() => { onChange(o.id); setOpen(false); }}
                data-testid={testId ? `${testId}-option-${o.id}` : undefined}
              >
                <span className={`truncate font-medium ${value === o.id ? "text-primary" : ""}`}>{o.label}</span>
                {o.sousTitre && (
                  <span className="text-xs text-muted-foreground truncate">{o.sousTitre}</span>
                )}
              </button>
            ))
          )}
        </div>
        {value && (
          <div className="border-t px-2 py-1.5">
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:text-destructive transition-colors text-left px-1 py-0.5"
              onClick={() => { onChange(null); setOpen(false); }}
            >
              Effacer la sélection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── buildEntiteOptions — map API records to EntiteOption ─────────────────────

function buildEntiteOptions(
  entiteType: "client" | "revendeur" | "prospect",
  clients: any[],
  resellers: any[],
  prospects: any[],
): EntiteOption[] {
  if (entiteType === "client") {
    return clients.map((c: any) => ({
      id: c.id,
      label: c.companyName || c.nomCommercial || c.id,
      sousTitre: c.numeroClient ? `#${c.numeroClient}` : undefined,
    }));
  }
  if (entiteType === "revendeur") {
    return resellers.map((r: any) => ({
      id: r.id,
      label: r.companyName || r.nomCommercial || r.id,
      sousTitre: r.ville || r.facturationVille || undefined,
    }));
  }
  return prospects.map((p: any) => ({
    id: p.id,
    label: p.societe || p.id,
    sousTitre: p.ville || undefined,
  }));
}

// ─── ContactLiensSection — shown inside contact detail (Organisation tab) ─────

interface ContactLiensSectionProps {
  contactId: string;
}

export function ContactLiensSection({ contactId }: ContactLiensSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState<{
    entiteType: "client" | "revendeur" | "prospect";
    entiteId: string | null;
    categorie: "membre_equipe" | "intervenant_exterieur";
    precisionLien: string;
    missionId: string | null;
    notes: string;
  }>({
    entiteType: "client",
    entiteId: null,
    categorie: "intervenant_exterieur",
    precisionLien: "",
    missionId: null,
    notes: "",
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    categorie: "membre_equipe" | "intervenant_exterieur";
    precisionLien: string;
    missionId: string | null;
    notes: string;
  }>({ categorie: "intervenant_exterieur", precisionLien: "", missionId: null, notes: "" });

  const { data: liens = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/contact-liens", contactId],
    queryFn: async () => {
      const res = await authFetch(`/api/contact-liens?contact_id=${contactId}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: clients = [], isLoading: loadingClients } = useQuery<any[]>({
    queryKey: ["/api/clients"],
    queryFn: async () => {
      const res = await authFetch("/api/clients");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: showForm,
  });

  const { data: resellers = [], isLoading: loadingResellers } = useQuery<any[]>({
    queryKey: ["/api/resellers"],
    queryFn: async () => {
      const res = await authFetch("/api/resellers");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: showForm,
  });

  const { data: prospectsList = [], isLoading: loadingProspects } = useQuery<any[]>({
    queryKey: ["/api/prospects"],
    queryFn: async () => {
      const res = await authFetch("/api/prospects");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: showForm,
  });

  const { data: missionsList = [] } = useQuery<any[]>({
    queryKey: ["/api/missions"],
    queryFn: async () => {
      const res = await authFetch("/api/missions");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: showForm,
  });

  const createMutation = useMutation({
    mutationFn: async (body: any) => {
      const res = await authFetch("/api/contact-liens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status === 409) {
        toast({ title: "Ce lien existe déjà", description: "Aucune duplication." });
        return null;
      }
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ["/api/contact-liens", contactId] });
      setShowForm(false);
      resetForm();
      toast({ title: "Lien ajouté" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/contact-liens/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Suppression impossible");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-liens", contactId] });
      toast({ title: "Lien supprimé" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, any> }) => {
      const res = await authFetch(`/api/contact-liens/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-liens", contactId] });
      setEditingId(null);
      toast({ title: "Lien mis à jour" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const resetForm = () => setForm({
    entiteType: "client", entiteId: null,
    categorie: "intervenant_exterieur", precisionLien: "", missionId: null, notes: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.entiteId) {
      toast({ title: "Sélectionnez une entité", variant: "destructive" });
      return;
    }
    createMutation.mutate({
      contact_id: contactId,
      entite_type: form.entiteType,
      entite_id: form.entiteId,
      categorie: form.categorie,
      precision_lien: emptyToNull(form.precisionLien),
      mission_id: emptyToNull(form.missionId),
      notes: emptyToNull(form.notes),
    });
  };

  const loadingEntites = form.entiteType === "client" ? loadingClients
    : form.entiteType === "revendeur" ? loadingResellers
    : loadingProspects;

  const entiteOptions = buildEntiteOptions(form.entiteType, clients, resellers, prospectsList);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
          Intervient pour
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => { setShowForm(v => !v); }}
          data-testid="btn-add-lien"
        >
          <Plus className="h-3 w-3" />
          Ajouter un lien
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border bg-muted/30 p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Type d'entité</Label>
              <Select
                value={form.entiteType}
                onValueChange={v => setForm(prev => ({ ...prev, entiteType: v as any, entiteId: null }))}
              >
                <SelectTrigger data-testid="select-entite-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="revendeur">Revendeur</SelectItem>
                  <SelectItem value="prospect">Prospect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Entité *</Label>
              <EntiteCombobox
                options={entiteOptions}
                value={form.entiteId}
                onChange={v => setForm(prev => ({ ...prev, entiteId: v }))}
                loading={loadingEntites}
                testId="combobox-entite-id"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Catégorie</Label>
            <Select value={form.categorie} onValueChange={v => setForm(prev => ({ ...prev, categorie: v as any }))}>
              <SelectTrigger data-testid="select-categorie-lien"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="membre_equipe">Équipe</SelectItem>
                <SelectItem value="intervenant_exterieur">Externe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Précision</Label>
            <Input
              value={form.precisionLien}
              onChange={e => setForm(prev => ({ ...prev, precisionLien: e.target.value }))}
              placeholder="ex : Ascensoriste"
              data-testid="input-precision-lien"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Mission liée (optionnel)</Label>
            <Select
              value={form.missionId ?? "aucune"}
              onValueChange={v => setForm(prev => ({ ...prev, missionId: v === "aucune" ? null : v }))}
            >
              <SelectTrigger data-testid="select-mission-lien">
                <SelectValue placeholder="Aucune mission" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aucune">Aucune mission</SelectItem>
                {missionsList.slice(0, 50).map((m: any) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.eventName || m.missionType || m.id} —{" "}
                    {m.startDate ? new Date(m.startDate).toLocaleDateString("fr-FR") : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes (optionnel)</Label>
            <Textarea
              value={form.notes}
              onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Informations complémentaires…"
              rows={2}
              data-testid="textarea-notes-lien"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowForm(false); resetForm(); }}>
              Annuler
            </Button>
            <Button type="submit" size="sm" disabled={createMutation.isPending} data-testid="btn-submit-lien">
              {createMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Enregistrer
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
        </div>
      ) : liens.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-1">Aucun lien d'intervention enregistré.</p>
      ) : (
        <div className="space-y-2">
          {liens.map((l: any) => (
            <div
              key={l.id}
              className="rounded-md border bg-card"
              data-testid={`lien-${l.id}`}
            >
              {editingId === l.id ? (
                <div className="px-3 py-2 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Entité : <span className="text-foreground">{l.entite?.nom || l.entite_type}</span>
                    <span className="ml-1 opacity-60">({l.entite_type})</span>
                  </p>
                  <div className="space-y-1">
                    <Label className="text-xs">Catégorie</Label>
                    <Select
                      value={editForm.categorie}
                      onValueChange={v => setEditForm(prev => ({ ...prev, categorie: v as any }))}
                    >
                      <SelectTrigger data-testid={`select-edit-categorie-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="membre_equipe">Équipe</SelectItem>
                        <SelectItem value="intervenant_exterieur">Externe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Précision</Label>
                    <Input
                      value={editForm.precisionLien}
                      onChange={e => setEditForm(prev => ({ ...prev, precisionLien: e.target.value }))}
                      placeholder="ex : Prestataire Ascensoriste ORONA"
                      data-testid={`input-edit-precision-${l.id}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes (optionnel)</Label>
                    <Textarea
                      value={editForm.notes}
                      onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      data-testid={`textarea-edit-notes-${l.id}`}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(null)}>Annuler</Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={editMutation.isPending}
                      data-testid={`btn-save-lien-${l.id}`}
                      onClick={() => editMutation.mutate({
                        id: l.id,
                        body: {
                          categorie: editForm.categorie,
                          precision_lien: emptyToNull(editForm.precisionLien),
                          mission_id: emptyToNull(editForm.missionId),
                          notes: emptyToNull(editForm.notes),
                        },
                      })}
                    >
                      {editMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2 px-3 py-2">
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">
                        {l.entite?.nom || l.entite_type}
                      </span>
                      <Badge variant="outline" className={`text-[10px] h-4 px-1 ${CATEGORIE_COLORS[l.categorie] || CATEGORIE_COLORS.intervenant_exterieur}`}>
                        {CATEGORIE_LABELS[l.categorie] || l.categorie}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize">
                        {l.entite_type}
                      </Badge>
                    </div>
                    {l.precisionLien && (
                      <p className="text-xs text-muted-foreground">{l.precisionLien}</p>
                    )}
                    {l.mission && (
                      <p className="text-xs text-muted-foreground">
                        Mission : {l.mission.libelle || l.mission.id}
                      </p>
                    )}
                    {l.notes && <p className="text-xs text-muted-foreground italic">{l.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setEditingId(l.id);
                        setEditForm({
                          categorie: l.categorie ?? "intervenant_exterieur",
                          precisionLien: l.precisionLien ?? "",
                          missionId: l.missionId ?? null,
                          notes: l.notes ?? "",
                        });
                      }}
                      data-testid={`btn-edit-lien-${l.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => deleteMutation.mutate(l.id)}
                      disabled={deleteMutation.isPending}
                      data-testid={`btn-delete-lien-${l.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── EntiteIntervenantsLienCard — level-module card (never inside render) ─────

interface EILienCardProps {
  l: any;
  onDelete: (id: string) => void;
  onReclasser: (id: string, categorie: string) => void;
  deletePending: boolean;
  reclasserPending: boolean;
  canEdit: boolean;
}

function EntiteIntervenantsLienCard({ l, onDelete, onReclasser, deletePending, reclasserPending, canEdit }: EILienCardProps) {
  return (
    <div
      className="flex items-start justify-between gap-2 rounded-md border bg-card px-3 py-2"
      data-testid={`intervenant-${l.id}`}
    >
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          {l.contact ? (
            <Link href={`/contacts/${l.contact.id}`} className="flex items-center gap-1 hover:underline">
              <span className="text-sm font-medium">
                {l.contact.prenom} {l.contact.nom}
              </span>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground">Contact inconnu</span>
          )}
          {l.contact?.poste && (
            <span className="text-xs text-muted-foreground">— {l.contact.poste}</span>
          )}
          <Badge variant="outline" className={`text-[10px] h-4 px-1 ${CATEGORIE_COLORS[l.categorie] || CATEGORIE_COLORS.intervenant_exterieur}`}>
            {CATEGORIE_LABELS[l.categorie] || l.categorie}
          </Badge>
        </div>
        {l.precisionLien && <p className="text-xs text-muted-foreground">{l.precisionLien}</p>}
        {l.mission && (
          <p className="text-xs text-muted-foreground">Mission : {l.mission.libelle || l.mission.id}</p>
        )}
        {l.notes && <p className="text-xs text-muted-foreground italic">{l.notes}</p>}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          <Select
            value={l.categorie ?? "intervenant_exterieur"}
            onValueChange={v => onReclasser(l.id, v)}
            disabled={reclasserPending}
          >
            <SelectTrigger
              className="h-6 w-auto text-[10px] gap-1 px-1.5 border-dashed"
              data-testid={`select-categorie-${l.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="membre_equipe">Équipe</SelectItem>
              <SelectItem value="intervenant_exterieur">Ext.</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={() => onDelete(l.id)}
            disabled={deletePending}
            data-testid={`btn-delete-intervenant-${l.id}`}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── EntiteIntervenants — shown inside entity detail (client/reseller/prospect) ─

interface EntiteIntervenantsProps {
  entiteType: "client" | "revendeur" | "prospect";
  entiteId: string;
}

export function EntiteIntervenants({ entiteType, entiteId }: EntiteIntervenantsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const canEdit = canDo(profile?.accessRole);

  const { data: liens = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/contact-liens/entite", entiteType, entiteId],
    queryFn: async () => {
      const res = await authFetch(
        `/api/contact-liens?entite_type=${entiteType}&entite_id=${entiteId}`
      );
      if (!res.ok) return [];
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/contact-liens/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Suppression impossible");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-liens/entite", entiteType, entiteId] });
      toast({ title: "Lien supprimé" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const reclasserMutation = useMutation({
    mutationFn: async ({ id, categorie }: { id: string; categorie: string }) => {
      const res = await authFetch(`/api/contact-liens/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categorie }),
      });
      if (!res.ok) throw new Error("Reclassement impossible");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-liens/entite", entiteType, entiteId] });
      toast({ title: "Catégorie mise à jour" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [addContactIds, setAddContactIds] = useState<string[]>([]);
  const [addCategorie, setAddCategorie] = useState<"membre_equipe" | "intervenant_exterieur">("intervenant_exterieur");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const createLienMutation = useMutation({
    mutationFn: async ({ contactId, categorie }: { contactId: string; categorie: string }) => {
      const res = await authFetch("/api/contact-liens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_id: contactId,
          entite_type: entiteType,
          entite_id: entiteId,
          categorie,
        }),
      });
      if (res.status === 409) {
        toast({ title: "Ce lien existe déjà", description: "Aucune duplication." });
        return null;
      }
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ["/api/contact-liens/entite", entiteType, entiteId] });
      setShowAddForm(false);
      setAddContactIds([]);
      setAddCategorie("intervenant_exterieur");
      toast({ title: "Contact rattaché" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!addContactIds[0]) {
      toast({ title: "Sélectionnez un contact", variant: "destructive" });
      return;
    }
    createLienMutation.mutate({ contactId: addContactIds[0], categorie: addCategorie });
  };

  const handleContactCreated = async (contact: Contact) => {
    setCreateModalOpen(false);
    try {
      const res = await authFetch("/api/contact-liens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_id: contact.id,
          entite_type: entiteType,
          entite_id: entiteId,
          categorie: addCategorie,
        }),
      });
      if (res.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
        queryClient.invalidateQueries({ queryKey: ["/api/contact-liens/entite", entiteType, entiteId] });
        toast({ title: "Contact créé", description: "Ce lien existait déjà — aucune duplication." });
      } else if (!res.ok) {
        throw new Error(await res.text());
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/contact-liens/entite", entiteType, entiteId] });
        queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
        setShowAddForm(false);
        setAddContactIds([]);
        setAddCategorie("intervenant_exterieur");
        toast({ title: "Contact créé et rattaché" });
      }
    } catch {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      toast({
        title: "Contact créé mais non rattaché",
        description: "Le rattachement a échoué. Liez manuellement depuis la fiche contact.",
        variant: "destructive",
      });
    }
  };

  const membres = liens.filter((l: any) => l.categorie === "membre_equipe");
  const intervenants = liens.filter((l: any) => !l.categorie || l.categorie === "intervenant_exterieur");

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Contacts liés</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => { setShowAddForm(v => !v); }}
          data-testid="btn-add-intervenant"
        >
          <Plus className="h-3 w-3" />
          Ajouter
        </Button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddSubmit} className="rounded-lg border bg-muted/30 p-3 space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Contact *</Label>
            <ContactSelector
              value={addContactIds}
              onChange={setAddContactIds}
              multiple={false}
              allowCreate={false}
              placeholder="Rechercher un contact…"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Catégorie</Label>
            <Select value={addCategorie} onValueChange={v => setAddCategorie(v as any)}>
              <SelectTrigger data-testid="select-add-categorie"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="membre_equipe">Équipe</SelectItem>
                <SelectItem value="intervenant_exterieur">Externe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={() => setCreateModalOpen(true)}
              data-testid="btn-create-new-contact"
            >
              + Créer un nouveau contact
            </button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => { setShowAddForm(false); setAddContactIds([]); setAddCategorie("intervenant_exterieur"); }}>
                Annuler
              </Button>
              <Button type="submit" size="sm" disabled={createLienMutation.isPending} data-testid="btn-submit-add-intervenant">
                {createLienMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Rattacher
              </Button>
            </div>
          </div>
        </form>
      )}

      <CreateContactModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleContactCreated}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-primary/70" />
              <span className="text-xs font-semibold text-primary/70 uppercase tracking-wide">Membres de l'équipe</span>
            </div>
            {membres.length === 0 ? (
              <p className="text-xs text-muted-foreground italic pl-5">Aucun membre de l'équipe renseigné.</p>
            ) : (
              <div className="space-y-1.5">
                {membres.map((l: any) => (
                  <EntiteIntervenantsLienCard
                    key={l.id}
                    l={l}
                    onDelete={id => deleteMutation.mutate(id)}
                    onReclasser={(id, cat) => reclasserMutation.mutate({ id, categorie: cat })}
                    deletePending={deleteMutation.isPending}
                    reclasserPending={reclasserMutation.isPending}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="border-t" />

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Intervenants extérieurs</span>
            </div>
            {intervenants.length === 0 ? (
              <p className="text-xs text-muted-foreground italic pl-5">Aucun intervenant extérieur rattaché.</p>
            ) : (
              <div className="space-y-1.5">
                {intervenants.map((l: any) => (
                  <EntiteIntervenantsLienCard
                    key={l.id}
                    l={l}
                    onDelete={id => deleteMutation.mutate(id)}
                    onReclasser={(id, cat) => reclasserMutation.mutate({ id, categorie: cat })}
                    deletePending={deleteMutation.isPending}
                    reclasserPending={reclasserMutation.isPending}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LienEntiteForm — optional section for CreateContactModal ─────────────────

export interface LienEntiteFormData {
  entiteType: "client" | "revendeur" | "prospect";
  entiteId: string | null;
  categorie: "membre_equipe" | "intervenant_exterieur";
  precisionLien: string;
  missionId: string | null;
}

interface LienEntiteFormProps {
  value: LienEntiteFormData;
  onChange: (v: LienEntiteFormData) => void;
  enabled: boolean;
  onToggle: () => void;
}

export function LienEntiteForm({ value, onChange, enabled, onToggle }: LienEntiteFormProps) {
  const { data: clients = [], isLoading: loadingClients } = useQuery<any[]>({
    queryKey: ["/api/clients"],
    queryFn: async () => {
      const res = await authFetch("/api/clients");
      if (!res.ok) return [];
      return res.json();
    },
    enabled,
  });

  const { data: resellers = [], isLoading: loadingResellers } = useQuery<any[]>({
    queryKey: ["/api/resellers"],
    queryFn: async () => {
      const res = await authFetch("/api/resellers");
      if (!res.ok) return [];
      return res.json();
    },
    enabled,
  });

  const { data: prospectsList = [], isLoading: loadingProspects } = useQuery<any[]>({
    queryKey: ["/api/prospects"],
    queryFn: async () => {
      const res = await authFetch("/api/prospects");
      if (!res.ok) return [];
      return res.json();
    },
    enabled,
  });

  const loadingEntites = value.entiteType === "client" ? loadingClients
    : value.entiteType === "revendeur" ? loadingResellers
    : loadingProspects;

  const entiteOptions = buildEntiteOptions(value.entiteType, clients, resellers, prospectsList);

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
        onClick={onToggle}
        data-testid="toggle-lien-entite"
      >
        <div className="flex items-center gap-1.5">
          <Link2 className="h-3 w-3" />
          Lien avec une entité (optionnel)
        </div>
        {enabled ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>

      {enabled && (
        <div className="p-3 space-y-3 border-t">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Type d'entité</Label>
              <Select
                value={value.entiteType}
                onValueChange={v => onChange({ ...value, entiteType: v as any, entiteId: null })}
              >
                <SelectTrigger data-testid="select-lien-entite-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="revendeur">Revendeur</SelectItem>
                  <SelectItem value="prospect">Prospect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Entité</Label>
              <EntiteCombobox
                options={entiteOptions}
                value={value.entiteId}
                onChange={v => onChange({ ...value, entiteId: v })}
                loading={loadingEntites}
                testId="combobox-lien-entite-id"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Catégorie</Label>
              <Select value={value.categorie} onValueChange={v => onChange({ ...value, categorie: v as any })}>
                <SelectTrigger data-testid="select-lien-categorie"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="membre_equipe">Équipe</SelectItem>
                  <SelectItem value="intervenant_exterieur">Externe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Précision</Label>
              <Input
                value={value.precisionLien}
                onChange={e => onChange({ ...value, precisionLien: e.target.value })}
                placeholder="ex : Prestataire Ascensoriste ORONA"
                data-testid="input-lien-precision"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
